import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Building2, Loader2, ArrowRight } from "lucide-react";

export default function Activar() {
    const nav = useNavigate();
    const { setUser } = useAuth() || {};
    const [email, setEmail] = useState("");
    const [phase, setPhase] = useState("email"); // email | password | already_active | not_authorized
    const [pendingName, setPendingName] = useState("");
    const [pendingUnidad, setPendingUnidad] = useState("");
    const [pass1, setPass1] = useState("");
    const [pass2, setPass2] = useState("");
    const [busy, setBusy] = useState(false);

    const checkEmail = async () => {
        if (!email.trim()) return toast.error("Ingresá tu email");
        setBusy(true);
        try {
            const r = await api.post("/activacion/check", { email: email.trim().toLowerCase() });
            if (r.data.status === "pendiente") {
                setPendingName(r.data.nombre || "");
                setPendingUnidad(r.data.unidad || "");
                setPhase("password");
            } else if (r.data.status === "activo") {
                setPhase("already_active");
            } else {
                setPhase("not_authorized");
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const setPassword = async () => {
        if (pass1.length < 6) return toast.error("La contraseña debe tener al menos 6 caracteres");
        if (pass1 !== pass2) return toast.error("Las contraseñas no coinciden");
        setBusy(true);
        try {
            const r = await api.post("/activacion/set-password", {
                email: email.trim().toLowerCase(),
                password: pass1,
            });
            localStorage.setItem("foresta_token", r.data.access_token);
            localStorage.setItem("foresta_user", JSON.stringify(r.data.user));
            if (setUser) setUser(r.data.user);
            toast.success(`¡Bienvenido${pendingName ? `, ${pendingName}` : ""}!`);
            nav("/");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-background">
            <header className="bg-primary text-primary-foreground p-5">
                <div className="flex items-center gap-3 max-w-md mx-auto">
                    <img src="/foresta-icon.png" alt="Foresta" className="h-10 w-10 object-contain bg-primary-foreground/10 rounded-md p-1" />
                    <div className="leading-tight">
                        <p className="font-heading text-lg font-extrabold tracking-tight">Foresta</p>
                        <p className="text-[11px] uppercase tracking-widest opacity-80">Reservas y Reclamos</p>
                    </div>
                </div>
            </header>

            <main className="flex-1 flex items-start justify-center p-5">
                <Card className="w-full max-w-md">
                    <CardContent className="p-6">
                        <div className="flex items-center gap-2.5 mb-5 pb-4 border-b border-border">
                            <div className="h-9 w-9 rounded-md bg-primary-soft text-primary grid place-items-center">
                                <Building2 className="h-4 w-4" />
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground uppercase tracking-widest">Activar acceso</p>
                                <p className="font-heading font-bold">Foresta — Maipú</p>
                            </div>
                        </div>

                        {phase === "email" && (
                            <>
                                <h1 className="font-heading text-xl font-bold mb-1">Tu email</h1>
                                <p className="text-sm text-muted-foreground mb-4">
                                    Ingresá el email con el que te dieron acceso al edificio.
                                </p>
                                <div className="space-y-2">
                                    <Label>Email</Label>
                                    <Input
                                        type="email"
                                        autoComplete="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        data-testid="activar-email"
                                        placeholder="tu@email.com"
                                    />
                                </div>
                                <Button
                                    onClick={checkEmail}
                                    disabled={busy || !email.trim()}
                                    className="w-full mt-5"
                                    data-testid="activar-check"
                                >
                                    {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Verificando…</> : <>Continuar <ArrowRight className="h-4 w-4 ml-1" /></>}
                                </Button>
                            </>
                        )}

                        {phase === "password" && (
                            <>
                                <h1 className="font-heading text-xl font-bold mb-1">Creá tu contraseña</h1>
                                <p className="text-sm text-muted-foreground mb-4">
                                    {pendingName && <>¡Hola <span className="font-semibold text-foreground">{pendingName}</span>! </>}
                                    {pendingUnidad && <>Te estamos activando para <span className="font-semibold text-foreground">{pendingUnidad}</span>. </>}
                                    Elegí una contraseña para tu cuenta.
                                </p>
                                <div className="space-y-3">
                                    <div className="space-y-2">
                                        <Label>Contraseña (mín. 6 caracteres)</Label>
                                        <Input type="password" value={pass1} onChange={(e) => setPass1(e.target.value)} data-testid="activar-pass1" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Repetir contraseña</Label>
                                        <Input type="password" value={pass2} onChange={(e) => setPass2(e.target.value)} data-testid="activar-pass2" />
                                    </div>
                                </div>
                                <Button onClick={setPassword} disabled={busy} className="w-full mt-5" data-testid="activar-submit">
                                    {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creando cuenta…</> : "Activar cuenta"}
                                </Button>
                            </>
                        )}

                        {phase === "not_authorized" && (
                            <>
                                <h1 className="font-heading text-xl font-bold mb-1">Email no autorizado</h1>
                                <p className="text-sm text-muted-foreground mb-4">
                                    Este email no está cargado en el sistema. Pedile a recepción/operaciones que verifique tu alta.
                                </p>
                                <Button variant="outline" onClick={() => { setEmail(""); setPhase("email"); }} className="w-full">
                                    Probar con otro email
                                </Button>
                            </>
                        )}

                        {phase === "already_active" && (
                            <>
                                <h1 className="font-heading text-xl font-bold mb-1">Cuenta ya activa</h1>
                                <p className="text-sm text-muted-foreground mb-4">
                                    Este usuario ya tiene acceso. Iniciá sesión con tu contraseña.
                                </p>
                                <Button onClick={() => nav("/login")} className="w-full" data-testid="activar-go-login">
                                    Ir a iniciar sesión
                                </Button>
                            </>
                        )}
                    </CardContent>
                </Card>
            </main>
        </div>
    );
}
